import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, MapPin, ArrowRight, Check, RefreshCcw, Building2, Users, AlertOctagon } from "lucide-react";

export default function CoveragePlannerPage() {
  const [includeReassignments, setIncludeReassignments] = useState(false);
  const [balanceWorkload, setBalanceWorkload] = useState(true);
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState([]);
  const [note, setNote] = useState(null);
  const [picks, setPicks] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [nps, setNps] = useState([]);
  const [gaps, setGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [tab, setTab] = useState("recommendations");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/nps");
        setNps(data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const loadGaps = async () => {
    setGapsLoading(true);
    try {
      const { data } = await api.get("/coverage/gaps");
      setGaps(data);
    } catch { toast.error("Could not load coverage gaps"); }
    finally { setGapsLoading(false); }
  };

  useEffect(() => {
    if (tab === "gaps" && !gaps && !gapsLoading) loadGaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const generate = async () => {
    setLoading(true);
    setPicks(new Set());
    setNote(null);
    try {
      const { data } = await api.post("/coverage/recommend", {
        include_reassignments: includeReassignments,
        balance_workload: balanceWorkload,
      });
      setRecs(data?.recommendations || []);
      setNote(data?.note || null);
      setHasRun(true);
      if (!data?.recommendations?.length && !data?.note) toast.info("AI returned no recommendations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate recommendations");
    } finally { setLoading(false); }
  };

  const togglePick = (fid) => {
    setPicks((prev) => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return next;
    });
  };

  const applicable = recs.filter((r) => r.recommended_np_id && r.is_change);
  const selectAll = () => setPicks(new Set(applicable.map((r) => r.facility_id)));
  const clearAll = () => setPicks(new Set());

  const applyPicks = async () => {
    if (picks.size === 0) return;
    const chosen = recs
      .filter((r) => picks.has(r.facility_id) && r.recommended_np_id)
      .map((r) => ({ facility_id: r.facility_id, np_id: r.recommended_np_id }));
    if (chosen.length === 0) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coverage/apply", { assignments: chosen });
      toast.success(`Applied ${data?.applied ?? chosen.length} assignments`);
      setPicks(new Set());
      generate();
    } catch { toast.error("Apply failed"); }
    finally { setApplying(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Tools</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2 flex items-center gap-3">
            <MapPin className="h-7 w-7 text-[#4A6B5D]"/> Coverage Planner
          </h1>
          <p className="text-sm text-[#5C5850] mt-1.5 max-w-2xl">
            AI reads each NP's address and every facility's location, then recommends the closest NP for each facility. Pick which suggestions to apply — nothing changes until you tap Apply.
          </p>
        </div>
      </div>

      <div className="mt-6 flex border-b border-[#E5E0D8] gap-1">
        <button
          onClick={() => setTab("recommendations")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === "recommendations" ? "border-[#4A6B5D] text-[#292723] font-medium" : "border-transparent text-[#8A847A] hover:text-[#292723]"}`}
          data-testid="tab-recommendations"
        >
          <Sparkles className="h-3.5 w-3.5 inline mr-1.5"/> Recommendations
        </button>
        <button
          onClick={() => setTab("gaps")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === "gaps" ? "border-[#4A6B5D] text-[#292723] font-medium" : "border-transparent text-[#8A847A] hover:text-[#292723]"}`}
          data-testid="tab-gaps"
        >
          <AlertOctagon className="h-3.5 w-3.5 inline mr-1.5"/> Coverage Gaps
          {gaps && gaps.total_gap_facilities > 0 && <span className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#F7E9E5] text-[#8A3E30]">{gaps.total_gap_facilities}</span>}
        </button>
      </div>

      {tab === "gaps" ? (
        <div className="mt-6" data-testid="gaps-panel">
          {gapsLoading && <div className="text-sm text-[#8A847A]">Loading gaps…</div>}
          {gaps && !gapsLoading && (
            <>
              <div className="bg-white border border-[#E5E0D8] rounded-md p-4 flex flex-wrap items-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#8A847A]">Facilities outside {gaps.threshold_miles}mi of any NP</div>
                  <div className="heading text-3xl font-bold text-[#8A3E30] font-mono">{gaps.total_gap_facilities}</div>
                </div>
                <div className="h-8 w-px bg-[#E5E0D8]"/>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#8A847A]">Not yet geocoded</div>
                  <div className="text-lg font-mono text-[#8A847A]">{gaps.facilities_without_coords}</div>
                </div>
                <div className="ml-auto text-xs text-[#8A847A]">A "gap" means no NP within {gaps.threshold_miles} miles as-the-crow-flies.</div>
              </div>

              {gaps.by_state.length === 0 ? (
                <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-10 text-center">
                  <Check className="h-8 w-8 mx-auto text-[#3B5C46] opacity-70"/>
                  <p className="text-sm text-[#5C5850] mt-3">Every geocoded facility has an NP within {gaps.threshold_miles} miles. 🎉</p>
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  {gaps.by_state.map((s) => (
                    <div key={s.state} className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden" data-testid={`gap-state-${s.state}`}>
                      <div className="bg-[#F7F5F0] px-4 py-2 flex items-center justify-between">
                        <div className="text-sm font-semibold text-[#292723]">{s.state}</div>
                        <div className="text-xs text-[#8A847A] font-mono">{s.count} facilit{s.count === 1 ? "y" : "ies"}</div>
                      </div>
                      {s.cities.map((c) => (
                        <div key={c.city} className="border-t border-[#E5E0D8] px-4 py-3">
                          <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center justify-between">
                            <span>{c.city}</span><span className="font-mono text-[#8A847A]">{c.count}</span>
                          </div>
                          <div className="mt-1.5 grid sm:grid-cols-2 gap-1.5">
                            {c.facilities.map((f) => (
                              <Link
                                key={f.facility_id}
                                to={`/facilities/${f.facility_id}`}
                                className="flex items-center justify-between text-sm px-3 py-1.5 rounded border border-[#E5E0D8] hover:bg-[#FAF8F3]"
                                data-testid={`gap-fac-${f.facility_id}`}
                              >
                                <span className="text-[#292723]">{f.name}</span>
                                {f.nearest_np ? (
                                  <span className="text-[11px] text-[#8A847A]">Nearest: {f.nearest_np.name} · <span className="font-mono">{f.nearest_np.distance_miles}mi</span></span>
                                ) : (
                                  <span className="text-[11px] text-[#8A3E30]">No NPs geocoded</span>
                                )}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <>

      <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeReassignments}
                onChange={(e) => setIncludeReassignments(e.target.checked)}
                className="h-4 w-4 accent-[#4A6B5D]"
                data-testid="coverage-include-reassign"
              />
              <span className="text-sm text-[#292723]">Include re-assignments</span>
              <span className="text-xs text-[#8A847A]">(also consider re-shuffling facilities already assigned to another NP)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={balanceWorkload}
                onChange={(e) => setBalanceWorkload(e.target.checked)}
                className="h-4 w-4 accent-[#4A6B5D]"
                data-testid="coverage-balance-workload"
              />
              <span className="text-sm text-[#292723]">Balance workload</span>
              <span className="text-xs text-[#8A847A]">(prefer proximity, but avoid overloading any single NP)</span>
            </label>
          </div>
          <Button
            onClick={generate}
            disabled={loading}
            className="bg-[#4A6B5D] hover:bg-[#3E594D]"
            data-testid="coverage-generate-btn"
          >
            {loading ? (
              <><RefreshCcw className="h-4 w-4 mr-2 animate-spin"/> Thinking…</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2"/> {hasRun ? "Regenerate" : "Generate recommendations"}</>
            )}
          </Button>
        </div>
      </div>

      {note && (
        <div className="mt-4 bg-[#FAF6EE] border border-[#E7DDC5] text-[#6B4E1E] text-sm rounded-md p-3" data-testid="coverage-note">{note}</div>
      )}

      {hasRun && recs.length > 0 && (
        <div className="mt-6 grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-sm text-[#5C5850]">
                <span className="font-semibold text-[#292723]">{applicable.length}</span> suggested change{applicable.length === 1 ? "" : "s"}
                <span className="text-[#8A847A]"> · {recs.length} facility{recs.length === 1 ? "" : "ies"} reviewed</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={selectAll} disabled={applicable.length === 0} className="text-xs text-[#4A6B5D] hover:underline disabled:opacity-40" data-testid="coverage-select-all">Select all changes</button>
                <button onClick={clearAll} disabled={picks.size === 0} className="text-xs text-[#8A847A] hover:underline disabled:opacity-40" data-testid="coverage-clear">Clear</button>
                <Button onClick={applyPicks} disabled={picks.size === 0 || applying} className="bg-[#4A6B5D] hover:bg-[#3E594D] h-9" data-testid="coverage-apply-btn">
                  <Check className="h-4 w-4 mr-2"/> {applying ? "Applying…" : `Apply ${picks.size || ""}`.trim()}
                </Button>
              </div>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="coverage-table">
                <thead>
                  <tr className="bg-[#F7F5F0]">
                    <th className="w-8 px-3 py-2"></th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Current NP</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Recommended NP</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {recs.map((r) => {
                    const canApply = !!r.recommended_np_id && r.is_change;
                    const checked = picks.has(r.facility_id);
                    return (
                      <tr key={r.facility_id} className={`border-t border-[#E5E0D8] ${checked ? "bg-[#F0EBE1]" : ""} ${canApply ? "" : "opacity-70"}`} data-testid={`coverage-row-${r.facility_id}`}>
                        <td className="px-3 py-2 align-top">
                          {canApply ? (
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePick(r.facility_id)}
                              className="h-4 w-4 accent-[#4A6B5D] cursor-pointer"
                              data-testid={`coverage-check-${r.facility_id}`}
                            />
                          ) : (
                            <span className="text-[10px] text-[#8A847A]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Link to={`/facilities/${r.facility_id}`} className="font-medium text-[#292723] hover:text-[#4A6B5D]">{r.facility_name}</Link>
                          <div className="text-xs text-[#8A847A]">{r.facility_location}</div>
                        </td>
                        <td className="px-3 py-2 align-top text-[#5C5850]">
                          {r.currently_assigned_np_name || <span className="italic text-[#8A847A]">Unassigned</span>}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {r.recommended_np_name ? (
                            <div className="flex items-center gap-1.5 text-[#292723]">
                              <ArrowRight className="h-3.5 w-3.5 text-[#4A6B5D]"/>
                              <Link to={`/nps/${r.recommended_np_id}`} className="font-medium hover:text-[#4A6B5D]">{r.recommended_np_name}</Link>
                              {!r.is_change && <span className="text-[10px] text-[#8A847A]">(already assigned)</span>}
                            </div>
                          ) : (
                            <span className="italic text-[#8A847A]">No confident match</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-[#5C5850] max-w-md">{r.reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <LoadSnapshot nps={nps} recs={recs} picks={picks} />
        </div>
      )}

      {hasRun && recs.length === 0 && !note && (
        <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-10 text-center">
          <Users className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
          <p className="text-sm text-[#8A847A] mt-3">AI returned no recommendations. Try enabling "Include re-assignments" or make sure your NPs and facilities have address / city / state filled in.</p>
        </div>
      )}

      {!hasRun && (
        <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-10 text-center">
          <Building2 className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
          <p className="text-sm text-[#8A847A] mt-3">Tap "Generate recommendations" to have GPT 5.6 Terra look at every NP address and every facility location, then suggest the closest NP for each site.</p>
          <p className="text-xs text-[#8A847A] mt-2">Best results when NPs have addresses and facilities have city + state (or full address).</p>
        </div>
      )}
        </>
      )}
    </div>
  );
}

function LoadSnapshot({ nps, recs, picks }) {
  // Compute before/after facility counts per NP given selected picks
  const { current, projected, max } = useMemo(() => {
    const cur = {};
    const proj = {};
    nps.forEach((n) => { cur[n.id] = n.facility_count || 0; proj[n.id] = n.facility_count || 0; });
    recs.forEach((r) => {
      if (!picks.has(r.facility_id)) return;
      if (r.recommended_np_id && proj[r.recommended_np_id] !== undefined) proj[r.recommended_np_id] += 1;
      if (r.currently_assigned_np_id && proj[r.currently_assigned_np_id] !== undefined) proj[r.currently_assigned_np_id] -= 1;
    });
    const m = Math.max(1, ...Object.values(cur), ...Object.values(proj));
    return { current: cur, projected: proj, max: m };
  }, [nps, recs, picks]);

  const rows = [...nps].sort((a, b) => (projected[b.id] || 0) - (projected[a.id] || 0));

  return (
    <div className="lg:col-span-1">
      <div className="bg-white border border-[#E5E0D8] rounded-md p-4 sticky top-6" data-testid="coverage-load-snapshot">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
          <Users className="h-3.5 w-3.5"/> Load per NP
        </div>
        <div className="mt-1 text-[10px] text-[#8A847A]">
          Grey = current · Green = after applying selected picks
        </div>
        <div className="mt-3 space-y-2.5 max-h-[560px] overflow-y-auto pr-1">
          {rows.map((n) => {
            const c = current[n.id] || 0;
            const p = projected[n.id] || 0;
            const cw = Math.round((c / max) * 100);
            const pw = Math.round((p / max) * 100);
            const delta = p - c;
            return (
              <div key={n.id} className="text-xs">
                <div className="flex items-center justify-between">
                  <div className="text-[#292723] truncate flex-1 mr-2">{n.name}</div>
                  <div className="font-mono text-[10px] text-[#5C5850] flex-shrink-0">
                    {c}
                    {delta !== 0 && (
                      <span className={delta > 0 ? "text-[#4A6B5D] ml-1" : "text-[#C17D6E] ml-1"}>
                        → {p} ({delta > 0 ? "+" : ""}{delta})
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1 h-2 bg-[#F0EBE1] rounded-sm relative overflow-hidden">
                  <div className="absolute top-0 left-0 bottom-0 bg-[#C7C0B4]" style={{ width: `${cw}%` }}/>
                  {delta !== 0 && (
                    <div className="absolute top-0 left-0 bottom-0 bg-[#4A6B5D] opacity-90" style={{ width: `${pw}%` }}/>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
